/**
 * The typed API client.
 *
 * Small on purpose. It attaches the credential, enforces same-origin, translates the
 * server's single error envelope into a typed error, and nothing else. Retry policy,
 * caching, and loading state belong to the query layer, which already does them properly.
 */

import { runtimeConfig } from '../config/runtime'

/** The error envelope every API error uses, at every status code. */
export interface ApiErrorBody {
  readonly code: string
  readonly message: string
  readonly details?: readonly {
    readonly field: string
    readonly issue: string
  }[]
  readonly correlationId?: string
}

/** How the interface should respond to a failure, derived from the status. */
export type ApiFailureKind =
  | 'unauthenticated'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'notImplemented'
  | 'unavailable'
  | 'network'
  | 'unknown'

export class ApiError extends Error {
  readonly status: number
  readonly kind: ApiFailureKind
  readonly code: string
  readonly details: readonly {
    readonly field: string
    readonly issue: string
  }[]
  readonly correlationId?: string

  constructor(
    status: number,
    body: ApiErrorBody | undefined,
    kind: ApiFailureKind,
  ) {
    super(body?.message ?? 'The request could not be completed.')
    this.name = 'ApiError'
    this.status = status
    this.kind = kind
    this.code = body?.code ?? 'UNKNOWN'
    this.details = body?.details ?? []
    if (body?.correlationId !== undefined) {
      this.correlationId = body.correlationId
    }
  }

  /** The issue recorded against a named field, if any. */
  issueFor(field: string): string | undefined {
    return this.details.find((detail) => detail.field === field)?.issue
  }

  /**
   * Whether retrying could plausibly succeed.
   *
   * A refusal is not a transient failure. Retrying a 403 produces another 403 and, on a
   * mutating route, a second audit entry recording a second refused attempt.
   */
  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'unavailable'
  }
}

function kindFor(status: number): ApiFailureKind {
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'notFound'
  if (status === 409) return 'conflict'
  if (status === 422) return 'validation'
  if (status === 501) return 'notImplemented'
  if (status === 503 || status === 502 || status === 504) return 'unavailable'
  return 'unknown'
}

/** Supplies the current credential. Implemented by the auth layer. */
export type CredentialSource = () => Promise<string | undefined>

export interface ApiClientOptions {
  readonly basePath?: string
  readonly credential?: CredentialSource
  readonly fetchImpl?: typeof fetch
}

/**
 * Join the base path and a resource path, refusing anything not same-origin.
 *
 * Enforced here as well as in configuration validation, because a caller could otherwise
 * pass an absolute URL directly and send the credential to another host.
 */
export function buildApiUrl(resourcePath: string, basePath: string): string {
  if (
    /^[a-z][a-z0-9+.-]*:\/\//i.test(resourcePath) ||
    resourcePath.startsWith('//')
  ) {
    throw new Error(
      `An API resource path must be same-origin, received "${resourcePath}". ` +
        'An absolute URL would send the credential to another host.',
    )
  }
  const suffix = resourcePath.startsWith('/')
    ? resourcePath
    : `/${resourcePath}`
  return `${basePath}${suffix}`
}

export class ApiClient {
  private readonly basePath: string
  private readonly credential: CredentialSource
  private readonly fetchImpl: typeof fetch

  constructor(options: ApiClientOptions = {}) {
    this.basePath = options.basePath ?? runtimeConfig().apiBasePath
    this.credential = options.credential ?? (async () => undefined)
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis)
  }

  async request<T>(
    path: string,
    init: RequestInit & {
      readonly query?: Record<string, string | number | undefined>
    } = {},
  ): Promise<T> {
    const { query, ...rest } = init
    let url = buildApiUrl(path, this.basePath)

    if (query !== undefined) {
      const search = new URLSearchParams()
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== '') {
          search.set(key, String(value))
        }
      }
      const rendered = search.toString()
      if (rendered !== '') {
        url = `${url}?${rendered}`
      }
    }

    const headers = new Headers(rest.headers)
    headers.set('Accept', 'application/json')
    if (rest.body !== undefined && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    const token = await this.credential()
    if (token !== undefined) {
      // Not a plain `Authorization` header: Azure Static Web Apps' own platform auth
      // layer silently overwrites that header with its own internal session token
      // before a request to /api/* ever reaches a Function -- confirmed directly
      // against a real deployment, where the credential this client set was never what
      // the backend received. X-DevEx-Authorization survives that layer untouched;
      // api/proxy promotes it back to a real Authorization header for the backend.
      headers.set('X-DevEx-Authorization', token)
    }

    let response: Response
    try {
      response = await this.fetchImpl(url, { ...rest, headers })
    } catch (cause) {
      // A transport failure is reported as such rather than as an unknown server error,
      // because the interface offers a retry for one and not the other.
      //
      // The original failure is kept as the cause. The user is shown the plain message;
      // the underlying DNS, TLS, or abort error is what makes the browser console useful
      // when somebody reports "it just says it could not be reached".
      const error = new ApiError(
        0,
        {
          code: 'NETWORK_UNAVAILABLE',
          message:
            'The service could not be reached. Check your connection and try again.',
        },
        'network',
      )
      error.cause = cause
      throw error
    }

    if (response.status === 204) {
      return undefined as T
    }

    const text = await response.text()
    const payload: unknown = text === '' ? undefined : safeParse(text)

    if (!response.ok) {
      const envelope =
        typeof payload === 'object' && payload !== null && 'error' in payload
          ? (payload as { error: ApiErrorBody }).error
          : undefined
      throw new ApiError(response.status, envelope, kindFor(response.status))
    }

    return payload as T
  }

  get<T>(
    path: string,
    query?: Record<string, string | number | undefined>,
  ): Promise<T> {
    return this.request<T>(path, query === undefined ? {} : { query })
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    // A non-JSON body from a JSON API usually means a proxy or gateway answered instead of
    // the application. Preserved as text so the cause is visible rather than swallowed.
    return {
      error: { code: 'MALFORMED_RESPONSE', message: text.slice(0, 200) },
    }
  }
}
